/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import Logo from "../components/Logo";
import Title from "../components/Title";
import { API_URL } from "../constants/config";
import { useUser } from "../contexts/UserContext";
import DashboardLayout from "../layouts/Dashboard";

const PromoCodeUsage = () => {
  const { id } = useParams();
  const [usageStats, setUsageStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useUser();

  // Define a color palette for the Pie Chart
  const COLORS = [
    "#8884d8", // Purple
    "#82ca9d", // Green
    "#ffbb28", // Yellow
    "#ff8042", // Orange
    "#00C49F", // Teal
    "#FF5733", // Red
    "#C70039", // Dark Red
    "#900C3F", // Maroon
    "#581845", // Dark Purple
  ];

  useEffect(() => {
    fetchUsageStats();
  }, [id]);

  useEffect(() => {
    if (user?.role !== "administrator") {
      navigate("/dashboard");
    }
  }, [navigate, user?.role]);

  const fetchUsageStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/promo-codes/${id}/usage-stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsageStats(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching usage stats", error);
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Area Chart - Trend Over Time
  const chartData = usageStats.map((stat) => ({
    date: new Date(stat.used_at).toLocaleDateString("en-GB", {
      month: "2-digit",
      year: "2-digit",
    }),
    discount_amount: parseFloat(stat.discount_amount),
    user_name: stat.user_name,
    used_at: stat.used_at,
    original_amount: parseFloat(stat.original_amount || 0),
    final_amount: parseFloat(stat.final_amount || 0),
  }));

  // Bar Chart - Total Discount Per User
  const userAggregatedData = usageStats.reduce((acc, curr) => {
    if (!acc[curr.user_name]) {
      acc[curr.user_name] = 0;
    }
    acc[curr.user_name] += parseFloat(curr.discount_amount);
    return acc;
  }, {});
  const userBarChartData = Object.entries(userAggregatedData).map(
    ([user_name, total_discount]) => ({
      user_name,
      total_discount: parseFloat(total_discount.toFixed(2)),
    })
  );

  // Pie Chart - Discount Share by User
  const pieChartData = userBarChartData;

  if (loading) {
    return (
      <div className="relative z-10">
        <div className="preloader-main w-full h-screen bg-white absolute opacity-90">
          <div className="preloader-div flex items-center justify-center flex-col h-full">
            <Logo animate={true} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <Box
        sx={{ padding: 3, maxHeight: "calc(100vh - 100px)", overflowY: "auto" }}
      >
        <div className="flex justify-between items-center">
          <Title title="Promo Code Usage Details" />
          <button
            onClick={handleBack}
            className="block px-4 py-1 text-sm border border-cf--primary text-cf--primary bg-white min-w-[90px] rounded-sm transition-all hover:text-white pt-sans-bold hover:bg-cf--primary mb-4"
          >
            Back
          </button>
        </div>

        {usageStats.length > 0 ? (
          <>
            {/* Chart 1: AreaChart - Usage Trend Over Time */}
            <div className="mb-10">
              <h3 className="text-lg font-semibold mb-2">
                Usage Trend Over Time
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length > 0) {
                        const {
                          user_name,
                          discount_amount,
                          used_at,
                          original_amount,
                          final_amount,
                        } = payload[0].payload;
                        const formattedTime = new Date(used_at).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          }
                        );

                        return (
                          <div
                            style={{
                              backgroundColor: "#fff",
                              border: "1px solid #ddd",
                              borderRadius: "5px",
                              padding: "10px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            <p>
                              <strong>User:</strong> {user_name}
                            </p>
                            <p>
                              <strong>Original Amount:</strong> PLN{" "}
                              {original_amount.toFixed(2)}
                            </p>
                            <p>
                              <strong>Discount Applied:</strong> PLN{" "}
                              {discount_amount.toFixed(2)}
                            </p>
                            <p>
                              <strong>Final Amount:</strong> PLN{" "}
                              {final_amount.toFixed(2)}
                            </p>
                            <p>
                              <strong>Used At:</strong> {formattedTime}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="discount_amount"
                    stroke="#8884d8"
                    fill="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Chart 2: BarChart - Total Discount by User */}
            <div className="mb-10">
              <h3 className="text-lg font-semibold mb-2">
                Total Discount by Each User
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userBarChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="user_name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_discount" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Chart 3: PieChart - Discount Share by User */}
            <div className="mb-10">
              <h3 className="text-lg font-semibold mb-2">
                Discount Share by User
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    dataKey="total_discount"
                    nameKey="user_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <p>No usage data available.</p>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default PromoCodeUsage;
